/**
 * Zoom-Cluster-Hilfsfunktionen für @o2d/whiteboard.
 *
 * Wenn der Nutzer Objekte bei sehr hohen Zoomstufen anlegt, würden die
 * Standard-Welteinheiten auf dem Bildschirm riesig erscheinen. Das
 * Cluster-System teilt die Viewport-Skala in vier diskrete Bänder ein.
 * Innerhalb eines Bandes gelten dieselben visuellen Standardgrößen; die
 * Welteinheiten werden jedoch durch den Cluster-Faktor geteilt, sodass
 * neu erstellte Objekte stets dieselbe Bildschirmgröße haben – unabhängig
 * davon, in welchem Zoom-Band der Nutzer gerade arbeitet.
 *
 * Band  Zoom-Bereich   Faktor
 * ──────────────────────────────
 *   1    < 150 %         1   (Referenz bei 100 %)
 *   2   150–299 %        2
 *   3   300–599 %        4
 *   4   ≥ 600 %          8
 *
 * AUSNAHME Schriftgröße (Anke, 2026-08-13): Der Cluster-Faktor (Bänder oben)
 * wird auf `textFontSize` NICHT angewendet — weder beim EXPLIZITEN Setzen
 * einer Stufe über einen Options-Button (`applyFontLevelToSelection`) noch
 * beim Umrechnen zurück in den aktiven Knopf (`o2d-svg-optionsbar.element.ts`).
 * Die 4 groben Bänder kompensierten nur den Sprung ZWISCHEN Bändern, nicht
 * die stufenlose Zoomänderung INNERHALB eines Bandes — eine mit "derselben"
 * Stufe gezeichnete Textbox konnte dadurch je nach exakter Zoomstufe zwischen
 * 38 und 77px groß werden (gemessen). Eine explizit gewählte
 * Schriftgrößenstufe ergibt seither immer denselben Welteinheiten-Wert,
 * unabhängig vom Zoom.
 *
 * Die STANDARD-Startgröße einer neu gezeichneten Textbox (`creationDefaults`,
 * kein Options-Button beteiligt) ist davon getrennt zu sehen: die ist
 * absichtlich weiterhin zoomabhängig, nur über einen anderen, stufenlosen
 * Mechanismus (`defaultFontIndexForScale`, Anke, 2026-08-14) statt der
 * groben Cluster-Bänder.
 */

export type ClusterFactor = 1 | 2 | 4 | 8;

/** Gibt den Cluster-Teiler für eine gegebene Viewport-Skala zurück (1,0 = 100 %). */
export function getClusterFactor(scale: number): ClusterFactor {
  if (scale >= 6.0) return 8;
  if (scale >= 3.0) return 4;
  if (scale >= 1.5) return 2;
  return 1;
}

/** Standardobjektbreite bei Cluster-Faktor 1 (100 % Zoom). */
export const DEFAULT_CREATION_WIDTH = 240;

// --- 24-Stufen-Schriftgrößenskala (PRD: 3×8 Größen) --------------------------
//
// Die Optionskreis-Schriftgröße hat 8 Obergruppen mit je 3 Feinstufen = 24
// Größen (Index 1…24). Nur die beiden Enden sind Stellschrauben; alles dazwischen
// ist EXPONENTIELL verteilt (Anke, 2026-08-14 — vorher linear/arithmetisch):
// von Stufe zu Stufe wächst die Größe um einen KONSTANTEN FAKTOR, nicht um
// einen konstanten Betrag. Start- (Stufe 1) und Endwert (Stufe 24) bleiben
// dabei exakt dieselben Zahlen wie zuvor. `action-fontsize-1-1` = kleinste,
// `action-fontsize-8-3` = größte.

/**
 * Kleinste Schriftgröße in Welteinheiten (Index 1 = `action-fontsize-1-1`).
 * Mehrfach angepasst (Anke, 2026-08-13/14): 8 → 20 → 3 → 4.
 */
export const FONT_SIZE_MIN_WU = 4;
/**
 * Größte Schriftgröße in Welteinheiten (Index 24 = `action-fontsize-8-3`).
 * Mehrfach angepasst (Anke, 2026-08-13/14): 128 → 380 → 1000.
 */
export const FONT_SIZE_MAX_WU = 1000;
/** Anzahl Feinstufen (8 Obergruppen × 3). */
export const FONT_SIZE_STEPS = 24;

/**
 * Klemmt einen (evtl. gerundeten) Index in den gültigen Bereich 1…24.
 *
 * Bugreport 2026-08-16 (review) — `Math.max`/`Math.min` klemmen `NaN` NICHT
 * (jeder Vergleich mit `NaN` ist `false`, `NaN` propagiert unverändert
 * durch). `defaultFontIndexForScale` liefert `NaN` für `scale <= 0`
 * (`Math.log10` von 0/negativ) — in der Praxis unerreichbar, da der
 * App-Zoom laut `viewport-math.helper.ts` immer in [0,1; 10] bleibt, aber
 * diese Funktion ist frei/exportiert aufrufbar und ihre eigene
 * Dokumentation verspricht "klemmt auf 1…24" ausnahmslos — das gilt jetzt
 * auch für `NaN`-Eingaben.
 */
function clampFontIndex(index: number): number {
  if (Number.isNaN(index)) return 1;
  return Math.max(1, Math.min(FONT_SIZE_STEPS, Math.round(index)));
}

/**
 * Verhältnis zwischen dem GRÖSSTEN und dem KLEINSTEN Wert der gesamten Skala
 * (Anke, 2026-08-14: exponentiell statt linear) — NICHT das Verhältnis
 * zwischen zwei benachbarten Stufen (das liegt bei
 * `FONT_SIZE_STEP_RATIO^(1/23)` ≈ 1,27). `fontIndexToWorldUnits` nutzt einen
 * BRUCH-Exponenten `t = (i-1)/23 ∈ [0,1]`, nicht diese Konstante direkt hoch
 * 23 — `FONT_SIZE_MIN_WU * FONT_SIZE_STEP_RATIO^t` ergibt für `t=0` exakt
 * `FONT_SIZE_MIN_WU` und für `t=1` exakt `FONT_SIZE_MAX_WU`; Start- und
 * Endwert bleiben unverändert, nur die 22 Zwischenwerte wachsen jetzt um
 * einen konstanten FAKTOR statt um einen konstanten Betrag.
 */
const FONT_SIZE_STEP_RATIO = FONT_SIZE_MAX_WU / FONT_SIZE_MIN_WU;

/** Welteinheiten-Schriftgröße für einen 1-basierten Feinstufen-Index (1…24). */
export function fontIndexToWorldUnits(index: number): number {
  const i = clampFontIndex(index);
  const t = (i - 1) / (FONT_SIZE_STEPS - 1);
  return FONT_SIZE_MIN_WU * Math.pow(FONT_SIZE_STEP_RATIO, t);
}

/**
 * Feinstufen-Index, mit dem eine NEUE Textbox startet — abhängig vom
 * aktuellen Zoom (Anke, 2026-08-14). Vorher war das ein fester Index
 * (`DEFAULT_FONT_INDEX`, entfernt); jetzt: bei starkem Reinzoomen startet
 * eine neue Box klein (sie wirkt auf dem Bildschirm ohnehin schon groß),
 * bei starkem Rauszoomen startet sie groß (sonst kaum sichtbar).
 *
 * Zwei explizite Ankerpunkte, LINEAR über `log10(scale)` interpoliert (nicht
 * über den rohen Zoom-Prozentwert): die Ankerpunkte liegen symmetrisch um
 * 100 % (×10 bzw. ÷10), eine lineare Interpolation über den Rohwert würde
 * 100 % Zoom stark zum 10-%-Anker hin verzerren statt in die Mitte zu legen.
 *   - 1000 % Zoom (scale 10)  → Stufe 4  ("2.1")
 *   - 100 %  Zoom (scale 1)   → Stufe 10 ("4.1") — Anke, 2026-10-02; vorher lag
 *     100 % ohne eigenen Anker in der Mitte (Stufe 12,5 → gerundet 5.1)
 *   - 10 %   Zoom (scale 0.1) → Stufe 21 ("7.3")
 * Zwischen benachbarten Ankern linear über `log10(scale)`.
 * Werte außerhalb [10 %, 1000 %] extrapolieren dieselbe Gerade; der spätere
 * `clampFontIndex`-Aufruf in `fontIndexToWorldUnits` fängt das auf 1…24 ab.
 */
const DEFAULT_INDEX_ANCHOR_HIGH_ZOOM = { scale: 10, index: 4 } as const;    // 1000 %
const DEFAULT_INDEX_ANCHOR_REF_ZOOM = { scale: 1, index: 10 } as const;     // 100 %
const DEFAULT_INDEX_ANCHOR_LOW_ZOOM = { scale: 0.1, index: 21 } as const;  // 10 %

/**
 * Feinstufen-Index (roh, NICHT gerundet), mit dem eine neue Textbox bei der
 * gegebenen Viewport-Skala starten soll. Siehe die Ankerpunkte oben.
 * `fontIndexToWorldUnits` rundet und klemmt beim Umrechnen in Welteinheiten.
 */
export function defaultFontIndexForScale(scale: number): number {
  // Above 100 % the segment REF→HIGH applies (and extrapolates past 1000 %),
  // below it the segment LOW→REF (extrapolating below 10 %).
  const [a, b] = scale >= DEFAULT_INDEX_ANCHOR_REF_ZOOM.scale
    ? [DEFAULT_INDEX_ANCHOR_REF_ZOOM, DEFAULT_INDEX_ANCHOR_HIGH_ZOOM]
    : [DEFAULT_INDEX_ANCHOR_LOW_ZOOM, DEFAULT_INDEX_ANCHOR_REF_ZOOM];
  const logA = Math.log10(a.scale);
  const slope = (b.index - a.index) / (Math.log10(b.scale) - logA);
  return a.index + slope * (Math.log10(scale) - logA);
}

/**
 * Nächstliegender Feinstufen-Index (1…24) zu einer Welteinheiten-Größe —
 * Umkehrfunktion zu `fontIndexToWorldUnits` (exponentiell, Anke, 2026-08-14).
 * `worldUnits <= FONT_SIZE_MIN_WU` (inkl. 0/negativ) klemmt direkt auf Stufe 1,
 * statt in `Math.log` eine undefinierte Zahl (Log von 0 oder Negativem) zu
 * erzeugen.
 */
export function worldUnitsToFontIndex(worldUnits: number): number {
  if (worldUnits <= FONT_SIZE_MIN_WU || FONT_SIZE_STEP_RATIO <= 1) return 1;
  const t = Math.log(worldUnits / FONT_SIZE_MIN_WU) / Math.log(FONT_SIZE_STEP_RATIO);
  return clampFontIndex(Math.round(t * (FONT_SIZE_STEPS - 1)) + 1);
}

/** Obergruppe (1…8), zu der ein Feinstufen-Index gehört (3 Stufen je Gruppe). */
export function fontIndexGroup(index: number): number {
  return Math.ceil(clampFontIndex(index) / 3);
}

/**
 * Cluster-angepasste Standardabmessungen für neu erstellte Objekte.
 * Alle Werte sind in Welteinheiten. Bei Cluster-Faktor F wird jeder Wert
 * durch F geteilt, sodass Objekte unabhängig von der aktuellen Zoomstufe
 * immer gleich groß auf dem Bildschirm erscheinen — AUSSER `textFontSize`,
 * die einer eigenen, stufenlosen Zoom-Abhängigkeit folgt (siehe
 * `defaultFontIndexForScale` oben).
 *
 * Bugreport 2026-08-16 (review) — `textFontSize` und die F-basierte
 * `textHeight` folgen zwei UNABHÄNGIGEN Skalen (stufenlos log-linear vs.
 * grob gestuft durch `F`, konstant `72/F` innerhalb eines Bandes). Exakt an
 * den Cluster-Band-Grenzen (F springt von einer Stufe zur nächsten, z.B. bei
 * scale 1.5/3.0/6.0) reißt die reine F-Höhe dabei schlagartig ab, während
 * `textFontSize` stetig weiterwächst — ohne Gegenmaßnahme konnte die
 * resultierende Standard-Schriftgröße dort bis zu 48% GRÖSSER als die
 * F-Höhe werden (Text läuft sofort über).
 *
 * Bugreport 2026-08-18 (review) — die ursprüngliche Gegenmaßnahme deckelte
 * `textFontSize` nach OBEN auf die F-Höhe (schrittweises Runtergehen auf der
 * 24-Stufen-Leiter). `F` ist aber für JEDEN Zoom < 150% konstant 1 (die
 * F-Höhe bleibt über den gesamten Bereich 100%→10% bei 72), während der
 * fein austarierte Zoom-Anker (`defaultFontIndexForScale`) dort bewusst bis
 * auf Stufe 21 (≈487 WU bei 10% Zoom) anwächst. Die Deckelung schnitt diesen
 * gesamten Bereich auf einen konstanten Wert (Stufe 13, ≈71 WU) glatt —
 * nicht nur an den drei schmalen Bandgrenzen, für die sie gedacht war. Die
 * fein austarierten 10%-Anker griffen dadurch faktisch nie.
 *
 * Fix: `textFontSize` bleibt unangetastet auf dem stufenlosen Zoom-Anker;
 * stattdessen wächst `textHeight` über die reine F-Höhe hinaus, wenn die
 * Schriftgröße sie überschreiten würde (`Math.max`) — die Box wird groß
 * genug für ihren Text gemacht, statt den fein austarierten Text auf die Box
 * zu stauchen. Das behebt weiterhin auch den Bandgrenzen-Überlauf von
 * 2026-08-16 (die Box wächst dort minimal statt der Text zu überlaufen),
 * ohne den 10%-Anker zu unterdrücken.
 *
 * Review 2026-08-18 (Linse A) — `textWidth` wuchs dabei zunächst NICHT mit:
 * bei 10% Zoom wäre die Box weiterhin nur 240 WU breit für eine bis zu 487 WU
 * große Schrift geblieben (die Breite expandiert, anders als die Höhe, nicht
 * automatisch mit dem Textinhalt — `textbox-svg-renderer.ts`s Auto-Expand hält
 * die Breite bewusst fest). `textWidth` wächst deshalb im selben Verhältnis
 * mit, sobald `textHeight` über die reine F-Höhe hinausgeht — es behält damit
 * das ursprüngliche 240:72-Seitenverhältnis der Referenzbox bei 100% Zoom bei.
 */
export function creationDefaults(scale: number): {
  textWidth:    number;
  textHeight:   number;
  textFontSize: number;
  stickySize:   number;
  rectWidth:    number;
  rectHeight:   number;
  personWidth:  number;
  personHeight: number;
  lineLength:   number;
  smileySize:   number;
} {
  const F = getClusterFactor(scale);
  // NICHT durch F geteilt (Anke, 2026-08-13 — Bugreport): eine EXPLIZIT
  // gewählte Schriftgrößenstufe (Options-Button) muss auf JEDEM Zoom
  // denselben Welteinheiten-Wert ergeben, sonst sehen zwei mit "derselben"
  // Stufe gezeichnete Textboxen bei gleichem Betrachtungszoom
  // unterschiedlich groß aus. Die STANDARD-Startgröße einer neu gezeichneten
  // Box ist aber bewusst zoomabhängig (Anke, 2026-08-14) — siehe
  // `defaultFontIndexForScale` — das ist ein separater, gewollter
  // Mechanismus, kein Rückfall in den alten Cluster-Faktor-Bug.
  const textFontSize = fontIndexToWorldUnits(defaultFontIndexForScale(scale));
  // Wächst über die reine F-Höhe hinaus, wenn `textFontSize` sie
  // überschreitet, statt den Text zu deckeln (siehe Docstring oben).
  const textHeight = Math.max(72 / F, textFontSize);
  return {
    // Wächst im selben 240:72-Seitenverhältnis mit `textHeight` mit (siehe
    // Docstring, Review 2026-08-18) — sonst bliebe die Box bei niedrigem
    // Zoom zu schmal für ihre eigene, deutlich größere Standard-Schrift.
    textWidth: Math.max(240 / F, (240 / 72) * textHeight),
    textHeight,
    textFontSize,
    stickySize:   180 / F,
    rectWidth:    120 / F,
    rectHeight:    80 / F,
    personWidth:   80 / F,
    personHeight: 110 / F,
    lineLength:    80 / F,
    smileySize:    60 / F,
  };
}

/**
 * Checkbox S/M/L sizes (Welteinheiten) at a given viewport scale — Stefan,
 * 2026-08-28: "die Größe der Checkbox ist im Moment unabhängig vom
 * Zoomfaktor... Größe M soll dann einfach der default-Schriftgröße beim
 * Zoomfaktor entsprechen. Größe S und L machen wir dann pauschal 30%
 * größer bzw. kleiner." M reuses EXACTLY the same stufenlose Zoom-Kopplung
 * as `creationDefaults().textFontSize` (`defaultFontIndexForScale` →
 * `fontIndexToWorldUnits`) rather than duplicating that math — same
 * "default text size at this zoom" value, just under a checkbox-specific
 * name. S/L are ±30% of M, no independent zoom formula of their own.
 *
 * Deliberately NOT run through the cluster factor `F` the way most other
 * `creationDefaults` values are — M IS the textFontSize value, which is
 * itself the one exception to cluster-factor scaling (see the module
 * docstring's "AUSNAHME Schriftgröße").
 *
 * Deliberately DIFFERENT from the text font-size options, though: those
 * freeze to a fixed world-unit value once explicitly picked and stay put
 * regardless of later zoom changes (see module docstring). The checkbox
 * S/M/L picker (its options-circle special case in
 * `o2d-svg-optionsbar.element.ts`, since the old actions-bar it originally
 * lived in has been removed) calls this function fresh on every render
 * instead of caching a value at pick-time, so it stays LIVE-coupled to the
 * current zoom — an intentional divergence from the text pattern, per the
 * request above.
 */
export function checkboxSizesForScale(scale: number): { S: number; M: number; L: number } {
  const M = fontIndexToWorldUnits(defaultFontIndexForScale(scale));
  return {
    S: M * 0.7,
    M,
    L: M * 1.3,
  };
}
